import { useState, useEffect, useCallback } from 'react';
import YouTubeAnalyzer from './YouTubeAnalyzer';
import SimulatorResultsView from './SimulatorResultsView';

const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

const HistoryPage = ({ buildUrl, user, isPro = false }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [tab, setTab] = useState('analysis'); // 'analysis' | 'simulator'

  const loadHistory = useCallback(async () => {
    if (!user?.email) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(buildUrl('/history'), {
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user.email,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.items || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [buildUrl, user]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSelect = (item) => {
    if (!item.analysisData) return;
    setSelectedItem(item);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setSelectedItem(null);
  };

  const filteredHistory = history.filter(item =>
    tab === 'simulator' ? item.mode === 'simulator' : item.mode !== 'simulator'
  );

  const analysisCount = history.filter(i => i.mode !== 'simulator').length;
  const simulatorCount = history.filter(i => i.mode === 'simulator').length;

  // Not signed in
  if (!user) {
    return (
      <div style={styles.page}>
        <div style={styles.centered}>
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#475569" style={{ marginBottom: '16px' }}>
            <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
          </svg>
          <h2 style={styles.emptyTitle}>Analysis History</h2>
          <p style={styles.emptyText}>Sign in to view your analysis history.</p>
          <button
            onClick={() => { window.location.hash = '#/signin'; }}
            style={styles.ctaBtn}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Viewing a selected analysis history item
  if (selectedItem && tab === 'analysis') {
    return (
      <YouTubeAnalyzer
        buildUrl={buildUrl}
        user={user}
        isPro={isPro}
        viewOnlyData={selectedItem.analysisData}
        onBackToHistory={handleBack}
      />
    );
  }

  // Viewing a selected simulator history item
  if (selectedItem && tab === 'simulator') {
    const data = selectedItem.analysisData;
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <button onClick={handleBack} style={styles.backBtn}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
            Back to History
          </button>

          <div style={{ marginBottom: '20px' }}>
            <h2 style={styles.pageTitle}>{selectedItem.title}</h2>
            <p style={styles.pageHint}>
              {new Date(selectedItem.createdAt).toLocaleDateString()} · {(data?.results || []).length} title{(data?.results || []).length !== 1 ? 's' : ''} · {data?.totalRounds || 10} rounds
            </p>
          </div>

          <SimulatorResultsView
            results={data?.results || []}
            aiAnalysis={data?.aiAnalysis}
            totalRounds={data?.totalRounds || 10}
          />
        </div>
      </div>
    );
  }

  // History list view
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.pageTitle}>History</h2>
            <p style={styles.pageHint}>{filteredHistory.length} {filteredHistory.length === 1 ? 'item' : 'items'} saved</p>
          </div>
          <button onClick={loadHistory} style={styles.refreshBtn} title="Refresh">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
          </button>
        </div>

        {/* Tab toggle */}
        <div style={styles.tabRow}>
          <button
            onClick={() => { setTab('analysis'); setSelectedItem(null); }}
            style={tab === 'analysis' ? styles.tabActive : styles.tab}
          >
            Analysis{analysisCount > 0 ? ` (${analysisCount})` : ''}
          </button>
          <button
            onClick={() => { setTab('simulator'); setSelectedItem(null); }}
            style={tab === 'simulator' ? styles.tabActive : styles.tab}
          >
            CTR Simulator{simulatorCount > 0 ? ` (${simulatorCount})` : ''}
          </button>
        </div>

        {loading && (
          <div style={styles.centered}>
            <p style={styles.emptyText}>Loading history...</p>
          </div>
        )}

        {!loading && filteredHistory.length === 0 && (
          <div style={styles.centered}>
            <svg viewBox="0 0 24 24" width="48" height="48" fill="#475569" style={{ marginBottom: '16px' }}>
              <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
            </svg>
            <h3 style={styles.emptyTitle}>
              {tab === 'simulator' ? 'No simulations yet' : 'No analyses yet'}
            </h3>
            <p style={styles.emptyText}>
              {tab === 'simulator'
                ? 'Run a CTR simulation to see your results here.'
                : 'Analyze a YouTube video or paste text to get started. Your results will appear here.'}
            </p>
            <button
              onClick={() => { window.location.hash = tab === 'simulator' ? '#/simulator' : '#/app'; }}
              style={styles.ctaBtn}
            >
              {tab === 'simulator' ? 'Go to Simulator' : 'Start Analyzing'}
            </button>
          </div>
        )}

        {!loading && filteredHistory.length > 0 && (
          <div style={styles.historyList}>
            {filteredHistory.map((item, i) => (
              <div
                key={item.id || i}
                style={styles.historyItem}
                onClick={() => handleSelect(item)}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                role="button"
                tabIndex={0}
              >
                {tab === 'analysis' && item.videoId ? (
                  <img
                    src={`https://img.youtube.com/vi/${item.videoId}/default.jpg`}
                    alt="" style={styles.historyThumb}
                  />
                ) : (
                  <div style={styles.historyTextIcon}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="#64748b">
                      {tab === 'simulator' ? (
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z"/>
                      ) : (
                        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                      )}
                    </svg>
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.historyTitle}>{item.title}</div>
                  <div style={styles.historyMeta}>
                    {tab === 'simulator' ? (
                      <>
                        {item.analysisData?.results?.length || 0} title{(item.analysisData?.results?.length || 0) !== 1 ? 's' : ''}
                        {item.analysisData?.results?.[0]?.ctr != null && ` · Best: ${(Math.max(...item.analysisData.results.map(r => r.ctr)) * 100).toFixed(1)}% CTR`}
                        {item.createdAt && ` · ${new Date(item.createdAt).toLocaleDateString()}`}
                      </>
                    ) : (
                      <>
                        {item.mode === 'url' ? 'YouTube' : 'Text'}
                        {item.topKeywords?.length > 0 && ` · ${item.topKeywords.slice(0, 3).join(', ')}`}
                        {item.createdAt && ` · ${new Date(item.createdAt).toLocaleDateString()}`}
                      </>
                    )}
                  </div>
                </div>
                {item.analysisData ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="#64748b" style={{ flexShrink: 0 }}>
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                  </svg>
                ) : (
                  <span style={styles.noDataBadge}>No data</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: '#e2e8f0',
  },
  container: {
    maxWidth: '960px', margin: '0 auto', padding: '40px 20px',
  },
  centered: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '40vh', textAlign: 'center',
  },
  headerRow: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    marginBottom: '16px',
  },
  pageTitle: {
    margin: 0, fontSize: '24px', fontWeight: '700', color: '#e2e8f0',
  },
  pageHint: {
    margin: '4px 0 0', fontSize: '14px', color: '#64748b',
  },
  refreshBtn: {
    padding: '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#94a3b8',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
  },
  tabRow: {
    display: 'flex', gap: '4px', marginBottom: '20px',
    background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '3px',
  },
  tab: {
    flex: 1, padding: '8px 16px', borderRadius: '8px', border: 'none',
    background: 'transparent', color: '#64748b', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
  },
  tabActive: {
    flex: 1, padding: '8px 16px', borderRadius: '8px', border: 'none',
    background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
  },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
    borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)', color: '#94a3b8', fontSize: '13px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '20px',
  },
  emptyTitle: {
    margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: '#e2e8f0',
  },
  emptyText: {
    margin: 0, fontSize: '14px', color: '#64748b', maxWidth: '400px', lineHeight: '1.6',
  },
  ctaBtn: {
    marginTop: '20px', padding: '10px 28px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  historyList: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  historyItem: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '12px 16px', borderRadius: '12px',
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
  },
  historyThumb: {
    width: '80px', height: '60px', borderRadius: '8px',
    objectFit: 'cover', flexShrink: 0,
  },
  historyTextIcon: {
    width: '80px', height: '60px', borderRadius: '8px',
    background: 'rgba(255,255,255,0.04)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  historyTitle: {
    fontSize: '15px', fontWeight: '600', color: '#e2e8f0',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  historyMeta: {
    fontSize: '13px', color: '#64748b', marginTop: '4px',
  },
  noDataBadge: {
    fontSize: '11px', color: '#475569', flexShrink: 0,
    padding: '2px 8px', borderRadius: '4px',
    background: 'rgba(255,255,255,0.04)',
  },
};

export default HistoryPage;
